import { axiosInstance } from "@/lib/api";
import { isDevMode } from "@/config/envvars";
import {
  ChatModel,
  ImageModel,
  ReasoningEffort,
  VideoModel,
} from "@/types/chat.type";
import { useQuery } from "@tanstack/react-query";

/** Must match backend BRAND_UNIVERSE_CHAT_DEFAULT_MODEL (envModel: cheap tier in dev/local, Sonnet in prod). Also used by Calendar, which shares that resolver. */
const BRAND_UNIVERSE_AUTO_MODEL_ID = isDevMode
  ? "gemini-3.5-flash-lite"
  : "claude-sonnet-5-5";

/** Must match backend SOCIAL_STRATEGY_CHAT_DEFAULT_MODEL (envModel: cheap tier in dev/local, Sonnet in prod). */
export const SOCIAL_STRATEGY_AUTO_MODEL_ID = isDevMode
  ? "gemini-3.5-flash-lite"
  : "claude-sonnet-5-5";

/** Must match backend WORKSHOP_CHAT_DEFAULT_MODEL (envModel: cheap tier in dev/local, Sonnet in prod). */
export const WORKSHOP_AUTO_MODEL_ID = isDevMode
  ? "gemini-3.5-flash-lite"
  : "claude-sonnet-5-5";

/** Highlighted pick in the brand universe model dropdown. */
export const BRAND_UNIVERSE_RECOMMENDED_MODEL_ID = "claude-sonnet-5-5";

const AUTO_REASONING_FALLBACK: ReasoningEffort[] = [
  "minimal",
  "low",
  "medium",
  "high",
];

const CHAT_MODEL_PROVIDER_ORDER = [
  "Anthropic",
  "OpenAI",
  "Google",
  "Moonshot",
] as const;

/**
 * Lower rank = more capable / higher in the picker.
 * Within each provider: frontier first, affordable last.
 */
const CHAT_MODEL_CAPABILITY_RANK: Record<string, number> = {
  // Anthropic
  "claude-opus-5-5": 0,
  "claude-sonnet-5-5": 1,
  "claude-haiku-5-5": 2,
  // Google
  "gemini-4-argon": 0,
  "gemini-3.8-flash": 1,
  // OpenAI
  "gpt-6.1-sol": 0,
  "gpt-6-luna": 1,
  // Moonshot
  "kimi-k3": 0,
  "kimi-k2.6": 1,
};

/** Retired picker ids that should resolve to the model that superseded them. */
const SUPERSEDED_CHAT_MODEL_IDS: Record<string, string> = {
  "gemini-3.1-pro-preview": "gemini-4-argon",
};

export function canonicalizeChatModelId(id: string): string {
  return SUPERSEDED_CHAT_MODEL_IDS[id] ?? id;
}

function sortByCapability(models: ChatModel[]): ChatModel[] {
  return [...models].sort((a, b) => {
    const rankA = CHAT_MODEL_CAPABILITY_RANK[a.id] ?? Number.MAX_SAFE_INTEGER;
    const rankB = CHAT_MODEL_CAPABILITY_RANK[b.id] ?? Number.MAX_SAFE_INTEGER;
    return rankA - rankB;
  });
}

export type ChatModelSection = {
  id: string;
  title: string;
  children: ChatModel[];
};

export function getChatModelProvider(modelId: string): string {
  if (modelId.startsWith("claude-")) return "Anthropic";
  if (modelId.startsWith("gemini-")) return "Google";
  if (modelId.startsWith("gpt-")) return "OpenAI";
  if (modelId.startsWith("kimi-")) return "Moonshot";
  return "Other";
}

export function groupChatModelsByProvider(
  models: ChatModel[],
): ChatModelSection[] {
  const sections: ChatModelSection[] = [];
  const autoModel = models.find((model) => model.id === "auto");

  if (autoModel) {
    sections.push({
      id: "recommended",
      title: "Recommended",
      children: [autoModel],
    });
  }

  const byProvider = new Map<string, ChatModel[]>();
  for (const model of models) {
    if (model.id === "auto") continue;
    const provider = getChatModelProvider(model.id);
    const group = byProvider.get(provider) ?? [];
    group.push(model);
    byProvider.set(provider, group);
  }

  for (const provider of CHAT_MODEL_PROVIDER_ORDER) {
    const children = byProvider.get(provider);
    if (!children?.length) continue;
    sections.push({
      id: provider.toLowerCase(),
      title: provider,
      children: sortByCapability(children),
    });
  }

  const other = byProvider.get("Other");
  if (other?.length) {
    sections.push({
      id: "other",
      title: "Other",
      children: sortByCapability(other),
    });
  }

  return sections;
}

export const useChatModels = (options?: { autoModelId?: string }) => {
  const autoModelId = options?.autoModelId ?? BRAND_UNIVERSE_AUTO_MODEL_ID;

  return useQuery({
    queryKey: ["chatModels", autoModelId],
    queryFn: async () => {
      const res = await axiosInstance.get<ChatModel[]>(`/llm/models`);
      const autoResolvedModel = res.data.find((m) => m.id === autoModelId);
      return [
        {
          id: "auto",
          name: "Auto",
          description: "Auto",
          isReasonsing: false,
          reasoningOptions:
            autoResolvedModel?.reasoningOptions ?? AUTO_REASONING_FALLBACK,
          contextWindow: autoResolvedModel?.contextWindow,
        },
        ...res.data,
      ];
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useImageModels = () => {
  return useQuery({
    queryKey: ["imageModels"],
    queryFn: async () => {
      const res = await axiosInstance.get<ImageModel[]>(
        `/llm/image-gen/models`,
      );
      return res.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useVideoModels = () => {
  return useQuery({
    queryKey: ["videoModels"],
    queryFn: async () => {
      const res = await axiosInstance.get<{ models: VideoModel[] }>(
        `/llm/video-gen/models`,
      );
      return res.data.models ?? [];
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
